import { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';

/** The custom property a sticky bar publishes on <html>: its rendered height
 *  (bead cca-rnghf; tokens.css documents it). Absent while no bar is sticky, so
 *  every reader carries a 0px fallback. */
export const ACTION_BAR_HEIGHT_VAR = '--cca-action-bar-height';

/** Each mounted bar's sticky height in px (0 while it is not sticky). The
 *  property carries the TALLEST, so two bars on one page cannot undo each other,
 *  and one leaving removes the property only when no other bar is sticky. */
const stickyHeights = new Map<object, number>();

/** Each STICKY bar's dock (cca-00fff) — the empty element it renders as its
 *  last child — in the order the bars became sticky. A bar leaves the map when
 *  it stops being sticky or unmounts, and one that turns sticky again joins at
 *  the end, so the order is always "most recently sticky last". */
const docks = new Map<object, HTMLElement>();

/** The dock the "Report a problem" launcher renders into now (null: none, so it
 *  keeps its fixed corner), and the launchers listening for a change of it. */
let currentDock: HTMLElement | null = null;
const dockListeners = new Set<() => void>();

/** Run at the moment the dock changes hands, BEFORE the snapshot changes and
 *  before React re-renders anything — while the launcher's old nodes are still
 *  on the page. ReportProblem notes there which of its own controls has focus,
 *  so it can put focus back once its new nodes mount (cca-00fff). */
const moveListeners = new Set<() => void>();

/** Write the tallest sticky height on <html>, or remove the property when there
 *  is none, then choose the dock to match — at once, or (`defer`, from inside a
 *  ResizeObserver callback) in the next frame. It runs only inside the bar's
 *  layout effect and observer, so a server render never reaches it. */
function publish(defer = false): void {
  const tallest = Math.max(0, ...stickyHeights.values());
  const root = document.documentElement.style;
  if (tallest > 0) root.setProperty(ACTION_BAR_HEIGHT_VAR, `${tallest}px`);
  else root.removeProperty(ACTION_BAR_HEIGHT_VAR);
  chooseDock(defer);
}

/** The dock is the bar whose height is published: the tallest sticky bar, and
 *  on a tie the one that became sticky most recently (the map's order, read
 *  with >=, so the later of two equals wins). So the launcher's panel, which
 *  takes the published height off its own height cap, takes off its own bar's.
 *  A bar measured at 0px offers none: nothing is published for it, and a
 *  launcher docked in a bar that is not drawn would vanish with it. Docking can
 *  only make the chosen bar taller (the launcher is one more item in its row),
 *  so it never hands the dock to another bar and back again. */
function pickDock(): HTMLElement | null {
  let next: HTMLElement | null = null;
  let height = 0;
  for (const [key, el] of docks) {
    const h = stickyHeights.get(key) ?? 0;
    if (h > 0 && h >= height) {
      next = el;
      height = h;
    }
  }
  return next;
}

/** Hand the dock over now, if it has changed: the move listeners first, then the
 *  snapshot, then the launchers. Listeners hear only a CHANGE, so a bar
 *  re-measured at the same size moves nothing. */
function handOver(): void {
  const next = pickDock();
  if (next === currentDock) return;
  for (const listener of moveListeners) listener();
  currentDock = next;
  for (const listener of dockListeners) listener();
}

/** The hand-off is immediate when a bar mounts or leaves (its layout effect, in
 *  the commit that draws or removes it), and one frame later when a
 *  ResizeObserver sees a bar turn sticky or not — a width change across the
 *  breakpoint (Tyson, cca-00fff). Handed over inside the observer's callback,
 *  the launcher's move resized the observed bar inside the observation loop, and
 *  WebKit raised "ResizeObserver loop completed with undelivered notifications"
 *  on every such change, which telemetry would log as a client_error. A
 *  microtask would not do: microtasks run inside the loop, at the callback's own
 *  checkpoint, and React's re-render for a store change already ran in one. The
 *  next animation frame runs before that frame's layout, observation and paint,
 *  so the observer then sees the bar's new size as an ordinary change. In the
 *  frame between, the launcher is still drawn where it was — in one place, never
 *  two and never none — and the snapshot does not change until the hand-off, so
 *  a re-render in that frame cannot move it either. A change the frame finds
 *  already undone hands nothing over. */
function chooseDock(defer: boolean): void {
  if (!defer) handOver();
  else if (pickDock() !== currentDock) requestAnimationFrame(handOver);
}

function subscribeDock(listener: () => void): () => void {
  dockListeners.add(listener);
  return () => {
    dockListeners.delete(listener);
  };
}

const dockSnapshot = (): HTMLElement | null => currentDock;

/** A server render has no bar measured, so it never docks. */
const serverDock = (): null => null;

/**
 * The dock of the sticky action bar on the page, or null when there is none — a
 * desktop row, a page with no bar, a server render (cca-00fff). ReportProblem
 * renders its launcher and panel into it, so on a phone the launcher is the
 * bar's trailing control rather than a second layer standing on the bar. The
 * bar and the launcher are mounted in different React subtrees (the bar deep in
 * a form, the launcher at each portal's root), so the hand-off is this
 * module-level store, read through useSyncExternalStore.
 */
export function useActionBarDock(): HTMLElement | null {
  return useSyncExternalStore(subscribeDock, dockSnapshot, serverDock);
}

/** Subscribe to the moment the dock changes hands, just before the snapshot
 *  changes (moveListeners, above); returns the unsubscribe. ReportProblem uses
 *  it to carry focus across the move. */
export function beforeActionBarDockMoves(listener: () => void): () => void {
  moveListeners.add(listener);
  return () => {
    moveListeners.delete(listener);
  };
}

/**
 * A row of actions that becomes a thumb-reachable sticky bar at the bottom of the
 * viewport on a phone (≤640px) and a plain inline row on desktop. Built on the
 * shared `.action-bar` utility in `@cca/ui/components`. Place it as the last block
 * of a long form so the primary action is always reachable without scrolling.
 *
 * WHILE IT IS STICKY it publishes its rendered height on <html> as
 * `--cca-action-bar-height` (bead cca-rnghf). components.css pads the
 * document's scroll port by it (cca-pftjr), so Tab and scrollIntoView stop a
 * focused control above the bar rather than under it (WCAG 2.2 SC 2.4.11), and
 * the "Report a problem" launcher's panel takes it off its height cap.
 *
 * And while it is sticky it offers its DOCK (cca-00fff): an empty element it
 * always renders as its last child, which components.css pushes to the row's
 * trailing edge and does not draw while empty. The launcher used to stand on
 * the bar, fixed 16px above it — exactly where the reservation above parks each
 * Tab-reached field, so on a phone a right-hand field was up to 88% covered
 * (390×900) and DateField's calendar button wholly hidden (320×256, 400% zoom),
 * on every forms-engine date question (Miranda r1 on #641). Now only one layer
 * owns the bottom edge: ReportProblem renders its launcher and panel INTO the
 * dock (useActionBarDock, above), in flow, as the bar's quiet trailing control,
 * and the bar's own controls are exempt from the reservation, so the docked
 * launcher covers nothing. With two sticky bars the dock is the one whose height
 * is published (pickDock). The dock is the bar's last child whatever the
 * children are: where the bar's own controls leave room it shares their row, and
 * where they do not it takes a row of its own at the bar's foot, and the bar is
 * that much taller. A full-width submit is the shared `.btn-block`, which in the
 * bar grows from its own width to fill what the launcher leaves rather than
 * claiming the whole row (components.css), so forms-web's public form and
 * Finance's claim form keep one row; vetting's wide bespoke Submit still wraps
 * the launcher onto a second row at a phone's width (Tyson, cca-00fff). At 400%
 * zoom the docked launcher is off screen until the form scrolls into view,
 * because the bar it docks in is. That is by design (Miranda, cca-00fff): do not
 * give it a corner fallback while the bar is off screen.
 *
 * A bare `.action-bar` sticks the same way but publishes nothing and offers no
 * dock, so it gets none of this — render this component instead. The
 * component reads the bar's COMPUTED `position` rather than restating the 640px breakpoint,
 * so `.action-bar` in components.css stays the one place that decides when the
 * bar is sticky. A ResizeObserver keeps the figure and the dock current (a row
 * that wraps, the phone's safe-area padding, a rotation across the breakpoint);
 * where there is none, the bar is measured once at mount. The property is
 * removed when no bar is sticky, and every reader falls back to 0px; the dock
 * goes with it, and the launcher returns to its fixed corner — so a page without
 * a bar, or a desktop, where the bar is a plain row, is exactly as before. Every
 * host that renders the bar gets this with no change of its own: forms-web's
 * public and anonymous form pages, Finance's claim form (cca-xero-web
 * ClaimFormPage), and the vetting applicant form (VettingFormPage, which
 * rendered a bare `.action-bar` until cca-iex84).
 */
export function StickyActionBar({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}): JSX.Element {
  const bar = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);

  // A layout effect, so a server render (renderToStaticMarkup) never reaches it,
  // and so the dock changes hands in the very commit that draws or removes the
  // bar: before that commit is painted, and — on the way out, where React runs a
  // layout cleanup before it takes the nodes away — while the launcher is still
  // on the page, so its focus can be noted (ReportProblem). As a passive effect,
  // a bar removed by a non-urgent update could be painted gone, launcher and
  // all, a frame before the corner took it back. React sets the refs on the
  // always-rendered <div>s in the commit, before any layout effect runs, so they
  // are never null here.
  useLayoutEffect(() => {
    const el = bar.current as HTMLDivElement;
    const dock = dockRef.current as HTMLDivElement;
    const key = {};
    // `fromObserver`: called back by the ResizeObserver, so the dock's hand-off
    // waits for the next frame (chooseDock); at mount it is immediate.
    const measure = (fromObserver: boolean): void => {
      // `sticky`, or the older `-webkit-sticky`; anything else is a plain row.
      const sticky = /sticky$/.test(window.getComputedStyle(el).position);
      stickyHeights.set(key, sticky ? Math.ceil(el.getBoundingClientRect().height) : 0);
      // Only a sticky bar offers its dock. Still sticky, it keeps its place in
      // the order; turned sticky again, it joins at the end.
      if (!sticky) docks.delete(key);
      else if (!docks.has(key)) docks.set(key, dock);
      publish(fromObserver);
    };
    measure(false);
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure(true));
    observer?.observe(el, { box: 'border-box' });
    return () => {
      observer?.disconnect();
      stickyHeights.delete(key);
      docks.delete(key);
      publish();
    };
  }, []);

  return (
    <div ref={bar} className={`action-bar${className ? ` ${className}` : ''}`} style={style}>
      {children}
      {/* The dock (cca-00fff): always rendered, always last, and empty unless the
          "Report a problem" launcher is rendered into it. React gives it no
          children of its own, so it never touches what the launcher puts there. */}
      <div ref={dockRef} className="action-bar-dock" />
    </div>
  );
}
